import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  resumeAudio, playLogoChime, playPop, playTwinkle,
  playRainbowWhoosh, playSunshine, playShapeBounce, playSubtitleReveal
} from '../utils/introSounds';

const LOGO_URL = '/logo_transparent.png';

/* 3D Block (letter/number) that flies out slowly from behind the logo */
const Block3D = ({ char, color, shadow, delay, endX, endY, size = 52 }) => (
  <motion.div
    initial={{ opacity: 0, x: 0, y: 0, scale: 0, rotate: -20 }}
    animate={{
      opacity: [0, 0.8, 1, 1],
      x: [0, endX * 0.3, endX * 0.7, endX],
      y: [0, endY * 0.3, endY * 0.7, endY],
      scale: [0, 0.4, 0.8, 1],
      rotate: [-20, 5, -3, 0],
    }}
    transition={{ delay, duration: 2.5, ease: [0.25, 0.46, 0.45, 0.94] }}
    className="absolute font-bold text-white flex items-center justify-center rounded-xl"
    style={{
      left: '50%',
      top: '50%',
      marginLeft: -size / 2,
      marginTop: -size / 2,
      width: size,
      height: size,
      background: `linear-gradient(145deg, ${color}, ${shadow})`,
      boxShadow: `4px 4px 0px ${shadow}, 6px 6px 12px rgba(0,0,0,0.15)`,
      fontSize: size * 0.5,
      fontFamily: "'Fredoka One', 'Nunito', sans-serif",
      zIndex: 10,
    }}
  >
    <motion.span
      animate={{ y: [0, -4, 0] }}
      transition={{ duration: 2.5, repeat: Infinity, delay: delay + 2, ease: 'easeInOut' }}
    >
      {char}
    </motion.span>
  </motion.div>
);

/* Shape that flies out from behind the logo */
const FlyingShape = ({ type, color, delay, endX, endY, size = 36 }) => {
  const shapeContent = {
    circle: <circle cx="20" cy="20" r="18" fill={color} opacity="0.7" />,
    triangle: <polygon points="20,2 38,38 2,38" fill={color} opacity="0.7" />,
    star: <path d="M20 2L24.5 14.5L38 14.5L27 22.5L30.5 36L20 28L9.5 36L13 22.5L2 14.5L15.5 14.5Z" fill={color} opacity="0.7" />,
    diamond: <polygon points="20,2 36,20 20,38 4,20" fill={color} opacity="0.7" />,
    heart: <path d="M20 35 C20 35 4 24 4 14 C4 8 8 4 14 4 C17 4 19 6 20 8 C21 6 23 4 26 4 C32 4 36 8 36 14 C36 24 20 35 20 35Z" fill={color} opacity="0.65" />,
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 0, y: 0, scale: 0, rotate: 0 }}
      animate={{
        opacity: [0, 0.6, 0.8, 0.7],
        x: [0, endX * 0.3, endX * 0.65, endX],
        y: [0, endY * 0.3, endY * 0.65, endY],
        scale: [0, 0.3, 0.7, 1],
        rotate: [0, 15, -10, 0],
      }}
      transition={{ delay, duration: 2.8, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="absolute"
      style={{
        left: '50%',
        top: '50%',
        marginLeft: -size / 2,
        marginTop: -size / 2,
        zIndex: 8,
      }}
    >
      <motion.svg
        width={size}
        height={size}
        viewBox="0 0 40 40"
        animate={{ rotate: [0, 360] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'linear', delay: delay + 2 }}
      >
        {shapeContent[type]}
      </motion.svg>
    </motion.div>
  );
};

/* Sparkle star */
const Sparkle = ({ delay, x, y, size = 16 }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0, rotate: 0 }}
    animate={{ opacity: [0, 1, 0.6, 1, 0], scale: [0, 1.2, 0.8, 1, 0], rotate: [0, 90, 180, 270, 360] }}
    transition={{ delay, duration: 2.5, ease: 'easeInOut' }}
    className="absolute"
    style={{ left: x, top: y }}
  >
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M12 0L14.5 9.5L24 12L14.5 14.5L12 24L9.5 14.5L0 12L9.5 9.5L12 0Z" fill="#FFD93D" />
    </svg>
  </motion.div>
);

/* Floating cloud */
const Cloud = ({ delay, startX, y, width = 120 }) => (
  <motion.div
    initial={{ opacity: 0, x: startX }}
    animate={{ opacity: [0, 0.5, 0.5, 0], x: [startX, startX + 60] }}
    transition={{ delay, duration: 8, ease: 'linear' }}
    className="absolute"
    style={{ top: y }}
  >
    <svg width={width} height={width * 0.4} viewBox="0 0 200 80" fill="white" opacity="0.6">
      <ellipse cx="60" cy="50" rx="55" ry="28" />
      <ellipse cx="110" cy="40" rx="50" ry="32" />
      <ellipse cx="150" cy="50" rx="40" ry="24" />
      <ellipse cx="85" cy="35" rx="45" ry="30" />
    </svg>
  </motion.div>
);

const AnimatedIntro = ({ onComplete }) => {
  const [phase, setPhase] = useState('enter');
  const [soundStarted, setSoundStarted] = useState(false);

  const startSounds = useCallback(() => {
    if (soundStarted) return;
    setSoundStarted(true);
    resumeAudio();

    // Logo chime (logo appears at delay 0.4s)
    setTimeout(() => playLogoChime(), 400);

    // Rainbow whoosh (rainbow starts at delay 0.8s)
    setTimeout(() => playRainbowWhoosh(), 800);

    // Sunshine warm tone (sunshine at delay 0.8s)
    setTimeout(() => playSunshine(), 900);

    // Letter/number pops — timed to match each block's delay
    const blockDelays = [1.0, 1.2, 1.4, 1.6, 1.8, 2.0, 2.2, 2.4];
    const pitches = [1.2, 1.1, 1.0, 0.9, 1.15, 1.05, 0.95, 0.85];
    blockDelays.forEach((d, i) => {
      setTimeout(() => playPop(pitches[i]), d * 1000);
    });

    // Shape bounces — timed to match each shape's delay
    const shapeDelays = [1.1, 1.3, 1.5, 1.7, 1.9, 2.1, 2.3, 2.5, 1.5, 1.7];
    const shapePitches = [1.3, 1.1, 0.9, 1.2, 1.0, 0.8, 1.15, 0.95, 1.25, 1.05];
    shapeDelays.forEach((d, i) => {
      setTimeout(() => playShapeBounce(shapePitches[i]), d * 1000);
    });

    // Twinkle sounds for sparkles
    [2.0, 2.3, 2.6, 2.9, 3.1].forEach((d) => {
      setTimeout(() => playTwinkle(), d * 1000);
    });

    // Subtitle reveal
    setTimeout(() => playSubtitleReveal(), 2800);
  }, [soundStarted]);

  useEffect(() => {
    // Start sounds on first interaction or immediately
    const handleInteraction = () => {
      startSounds();
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
    };

    // Try starting immediately (works if autoplay is allowed)
    setTimeout(() => startSounds(), 100);

    // Fallback: start on first interaction
    window.addEventListener('click', handleInteraction);
    window.addEventListener('touchstart', handleInteraction);

    const revealTimer = setTimeout(() => setPhase('reveal'), 600);
    const exitTimer = setTimeout(() => setPhase('exit'), 5500);
    const completeTimer = setTimeout(() => onComplete(), 6500);
    return () => {
      clearTimeout(revealTimer);
      clearTimeout(exitTimer);
      clearTimeout(completeTimer);
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
    };
  }, [onComplete, startSounds]);

  /* Letters and numbers — fly outward slowly from center */
  const blocks = [
    { char: 'A', color: '#F28B82', shadow: '#c96b65', endX: -300, endY: -170, delay: 1.0, size: 52 },
    { char: 'B', color: '#81C9E8', shadow: '#5fa8c9', endX: 290, endY: -160, delay: 1.2, size: 48 },
    { char: 'C', color: '#A8D88E', shadow: '#7fb86b', endX: -260, endY: 150, delay: 1.4, size: 44 },
    { char: '1', color: '#F5C869', shadow: '#d4a84a', endX: 270, endY: 140, delay: 1.6, size: 46 },
    { char: '2', color: '#C5A3F1', shadow: '#a07fd4', endX: -330, endY: 15, delay: 1.8, size: 42 },
    { char: '3', color: '#FF9F7F', shadow: '#d97a5c', endX: 320, endY: -5, delay: 2.0, size: 44 },
    { char: 'D', color: '#88DFCF', shadow: '#5cb8a7', endX: -170, endY: -200, delay: 2.2, size: 40 },
    { char: '4', color: '#FFB3C6', shadow: '#d98fa3', endX: 170, endY: 190, delay: 2.4, size: 40 },
  ];

  /* Shapes — fly outward slowly from behind logo */
  const shapes = [
    { type: 'circle', color: '#FFB3B3', endX: -220, endY: -120, delay: 1.1, size: 32 },
    { type: 'triangle', color: '#A0D4F1', endX: 240, endY: -100, delay: 1.3, size: 28 },
    { type: 'star', color: '#FFE88D', endX: -180, endY: 120, delay: 1.5, size: 30 },
    { type: 'diamond', color: '#C5A3F1', endX: 200, endY: 100, delay: 1.7, size: 26 },
    { type: 'heart', color: '#FFB3C6', endX: -350, endY: -80, delay: 1.9, size: 28 },
    { type: 'circle', color: '#A8E6A1', endX: 360, endY: 80, delay: 2.1, size: 30 },
    { type: 'star', color: '#FFD6A0', endX: 100, endY: -220, delay: 2.3, size: 24 },
    { type: 'triangle', color: '#88DFCF', endX: -100, endY: 220, delay: 2.5, size: 26 },
    { type: 'diamond', color: '#F28B82', endX: 380, endY: -140, delay: 1.5, size: 22 },
    { type: 'heart', color: '#81C9E8', endX: -380, endY: 140, delay: 1.7, size: 24 },
  ];

  return (
    <AnimatePresence>
      {phase !== 'done' && (
        <motion.div
          initial={{ opacity: 1 }}
          animate={{ opacity: phase === 'exit' ? 0 : 1 }}
          transition={{ duration: 0.8 }}
          className="fixed inset-0 z-[100] overflow-hidden flex items-center justify-center"
          style={{ background: 'linear-gradient(180deg, #D6EEFF 0%, #E8F4FD 40%, #F0F8FF 70%, #FFF9F0 100%)' }}
          data-testid="animated-intro"
        >
          {/* Soft background clouds */}
          <Cloud delay={0} startX={-50} y="8%" width={160} />
          <Cloud delay={0.3} startX="70%" y="12%" width={130} />
          <Cloud delay={0.6} startX="20%" y="5%" width={100} />
          <Cloud delay={0.2} startX="85%" y="20%" width={90} />


          {/* Sunshine — rises up together with the rainbow */}
          <motion.div
            initial={{ opacity: 0, y: 200, scale: 0 }}
            animate={{
              opacity: phase === 'exit' ? 0 : [0, 0.85, 0.75],
              y: phase === 'exit' ? -80 : [200, -15, 0],
              scale: phase === 'exit' ? 2 : [0, 1.2, 1],
            }}
            transition={{ delay: 0.8, duration: 2.5, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="absolute top-[3%] right-[8%] z-10"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
              className="w-24 h-24 md:w-36 md:h-36"
            >
              <svg viewBox="0 0 120 120" fill="none" className="w-full h-full">
                {/* Sun rays */}
                {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((angle) => (
                  <line
                    key={angle}
                    x1="60" y1="60"
                    x2={60 + 52 * Math.cos((angle * Math.PI) / 180)}
                    y2={60 + 52 * Math.sin((angle * Math.PI) / 180)}
                    stroke="#FFD93D"
                    strokeWidth="4"
                    strokeLinecap="round"
                    opacity="0.6"
                  />
                ))}
                {/* Sun glow */}
                <circle cx="60" cy="60" r="28" fill="#FFE066" opacity="0.3" />
                {/* Sun body */}
                <circle cx="60" cy="60" r="22" fill="#FFD93D" opacity="0.8" />
                {/* Sun face shine */}
                <circle cx="54" cy="56" r="4" fill="#FFF3B0" opacity="0.6" />
              </svg>
            </motion.div>
          </motion.div>

          {/* Rainbow sweep — rises from left to right behind the logo */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{
              opacity: phase === 'exit' ? 0 : [0, 0.7, 0.6],
            }}
            transition={{ delay: 0.8, duration: 1.5 }}
            className="absolute z-10"
            style={{ width: '90vw', maxWidth: 850, aspectRatio: '2/1', marginTop: '-38%' }}
          >
            <svg viewBox="0 0 800 400" fill="none" className="w-full h-full overflow-visible">
              <defs>
                <clipPath id="rainbow-reveal">
                  <motion.rect
                    initial={{ width: 0 }}
                    animate={{ width: 800 }}
                    transition={{ delay: 0.8, duration: 2.5, ease: [0.25, 0.46, 0.45, 0.94] }}
                    x="0" y="0" height="400"
                  />
                </clipPath>
              </defs>
              <g clipPath="url(#rainbow-reveal)">
                <path d="M30 390 Q400 -80 770 390" stroke="#FF6B6B" strokeWidth="20" strokeLinecap="round" fill="none" opacity="0.5" />
                <path d="M55 390 Q400 -55 745 390" stroke="#FF9F43" strokeWidth="18" strokeLinecap="round" fill="none" opacity="0.5" />
                <path d="M80 390 Q400 -30 720 390" stroke="#FECA57" strokeWidth="16" strokeLinecap="round" fill="none" opacity="0.5" />
                <path d="M105 390 Q400 -5 695 390" stroke="#48DBFB" strokeWidth="14" strokeLinecap="round" fill="none" opacity="0.45" />
                <path d="M130 390 Q400 20 670 390" stroke="#0ABDE3" strokeWidth="12" strokeLinecap="round" fill="none" opacity="0.4" />
                <path d="M155 390 Q400 45 645 390" stroke="#A29BFE" strokeWidth="10" strokeLinecap="round" fill="none" opacity="0.35" />
              </g>
            </svg>
          </motion.div>

          {/* Shapes flying out from behind the logo */}
          {shapes.map((s, i) => (
            <FlyingShape key={`shape-${i}`} {...s} />
          ))}

          {/* 3D Letters and Numbers — fly out slowly from behind the logo */}
          {blocks.map((b) => (
            <Block3D key={b.char + b.endX} {...b} />
          ))}

          {/* LOGO — the star of the show, z-index above blocks and shapes */}
          <motion.div
            initial={{ opacity: 0, scale: 0.2, y: 40 }}
            animate={{
              opacity: phase === 'exit' ? 0 : 1,
              scale: phase === 'exit' ? 1.15 : [0.2, 1.08, 0.96, 1],
              y: phase === 'exit' ? -20 : [40, -8, 4, 0],
            }}
            transition={{
              delay: phase === 'exit' ? 0 : 0.4,
              duration: phase === 'exit' ? 0.6 : 1.2,
              ease: phase === 'exit' ? 'easeIn' : [0.34, 1.56, 0.64, 1],
            }}
            className="relative z-20 px-4"
          >
            <motion.img
              src={LOGO_URL}
              alt="Hooyo's Place Early Education & Childcare"
              className="w-[90vw] max-w-[680px] md:max-w-[720px] h-auto"
              style={{
                filter: 'drop-shadow(0 8px 30px rgba(0,0,0,0.1))',
              }}
              animate={phase !== 'exit' ? { y: [0, -6, 0] } : {}}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 2.5 }}
              data-testid="intro-logo"
            />
          </motion.div>

          {/* Sparkle stars */}
          <Sparkle delay={2.0} x="18%" y="28%" size={20} />
          <Sparkle delay={2.3} x="78%" y="25%" size={16} />
          <Sparkle delay={2.6} x="22%" y="68%" size={14} />
          <Sparkle delay={2.9} x="75%" y="65%" size={18} />
          <Sparkle delay={2.5} x="50%" y="18%" size={22} />
          <Sparkle delay={3.1} x="40%" y="75%" size={15} />
          <Sparkle delay={2.7} x="60%" y="72%" size={17} />
          <Sparkle delay={2.2} x="30%" y="20%" size={13} />

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{
              opacity: phase === 'exit' ? 0 : [0, 0, 1],
              y: phase === 'exit' ? 10 : [15, 15, 0],
            }}
            transition={{ delay: 2.8, duration: 0.8, ease: 'easeOut' }}
            className="absolute bottom-[15%] md:bottom-[18%] text-center font-nunito font-semibold text-base md:text-lg tracking-wide z-20"
            style={{ color: 'hsl(215, 45%, 30%)' }}
            data-testid="intro-subtitle"
          >
            Where Little Minds Grow
          </motion.p>

          {/* Skip Button */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.7 }}
            whileHover={{ opacity: 1, scale: 1.05 }}
            transition={{ delay: 1.5 }}
            onClick={onComplete}
            className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-sm text-foreground hover:bg-white px-5 py-2.5 rounded-full text-sm font-bold z-40 shadow-lg border border-black/5"
            data-testid="intro-skip-btn"
          >
            Skip
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AnimatedIntro;
